/**
 * CCPA 45-Day Breach Notification Playbook UI
 * Feature: US-157 (Incident Response Playbooks for US Data Breaches)
 * Task: T-157-002
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Playbook,
  IncidentStage,
  IRRole,
  Severity,
  INCIDENT_STAGES,
  IR_ROLES,
  INCIDENT_SEVERITY,
  STAGE_DESCRIPTIONS,
  createPlaybook,
  transitionStage,
  assignRole,
  addAuditLogEntry,
  savePlaybook,
  getPlaybooks,
  getNextStage,
  calculateDaysRemaining,
} from './engine';

export interface CcpaPlaybookProps {
  initialPlaybook?: Playbook;
  onPlaybookUpdated?: (playbook: Playbook) => void;
}

const CCPA_STAGE_CHECKLISTS: Record<IncidentStage, string[]> = {
  DETECTION: [
    'Confirm incident and establish initial detection timestamp',
    'Identify affected personal information (PII / Sensitive PII)',
    'Log initial incident parameters in secure audit ledger',
  ],
  CONTAINMENT: [
    'Isolate affected endpoints, databases, and application servers',
    'Rotate API keys, SSH keys, and privileged administrator credentials',
    'Halt ongoing unauthorized data transfers and exfiltration pathways',
  ],
  ASSESSMENT: [
    'Quantify exact count of affected California residents',
    'Determine if unencrypted sensitive personal information was accessed',
    'Evaluate private right of action exposure under CCPA § 1798.150',
  ],
  NOTIFICATION: [
    'Draft breach notification letters conforming to Cal. Civ. Code § 1798.82',
    'Provide mandatory notice to California Attorney General if >= 500 CA residents',
    'Dispatch notices to affected consumers within statutory 45-day window',
  ],
  REMEDIATION: [
    'Patch root vulnerability and apply security hardening controls',
    'Upgrade encryption algorithms and access control policies',
    'Conduct post-patch vulnerability scan and penetration test',
  ],
  POST_MORTEM: [
    'Produce formal post-incident technical review and lessons learned',
    'Archive statutory compliance documentation and notification proofs',
    'Update CCPA Incident Response Playbook and train response team',
  ],
};

export const CcpaPlaybook: React.FC<CcpaPlaybookProps> = ({
  initialPlaybook,
  onPlaybookUpdated,
}) => {
  // Initialize playbook from prop, localStorage, or default
  const [playbook, setPlaybook] = useState<Playbook>(() => {
    if (initialPlaybook) return initialPlaybook;
    const stored = getPlaybooks().filter((p) => p.type === 'CCPA_BREACH');
    if (stored.length > 0) return stored[0];
    const defaultPb = createPlaybook(
      'CCPA_BREACH',
      'HIGH',
      'CCPA Personal Information Security Breach',
      'California Consumer Privacy Act breach response workflow under Cal. Civ. Code § 1798.82.'
    );
    savePlaybook(defaultPb);
    return defaultPb;
  });

  // Creation form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newSeverity, setNewSeverity] = useState<Severity>('HIGH');
  const [newDescription, setNewDescription] = useState('');
  const [newDetectionDate, setNewDetectionDate] = useState(
    new Date().toISOString().substring(0, 10)
  );

  // Role assignment state
  const [roleInputs, setRoleInputs] = useState<Record<IRRole, string>>({
    INCIDENT_COMMANDER: '',
    LEGAL_COUNSEL: '',
    TECHNICAL_LEAD: '',
    COMMUNICATIONS_LEAD: '',
    DPO: '',
  });

  // Manual audit log entry state
  const [manualNote, setManualNote] = useState('');
  const [transitionError, setTransitionError] = useState<string | null>(null);

  // Persist whenever playbook changes
  useEffect(() => {
    savePlaybook(playbook);
    if (onPlaybookUpdated) {
      onPlaybookUpdated(playbook);
    }
  }, [playbook, onPlaybookUpdated]);

  const daysRemaining = useMemo(() => {
    return calculateDaysRemaining(playbook.deadline);
  }, [playbook.deadline]);

  const nextStage = useMemo(() => {
    return getNextStage(playbook.stage);
  }, [playbook.stage]);

  const handleCreateIncident = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim()) return;

    const createdAt = newDetectionDate
      ? new Date(newDetectionDate).toISOString()
      : new Date().toISOString();

    const created = createPlaybook(
      'CCPA_BREACH',
      newSeverity,
      newTitle.trim(),
      newDescription.trim() || 'CCPA statutory breach incident',
      { createdAt }
    );

    savePlaybook(created);
    setPlaybook(created);
    setNewTitle('');
    setNewDescription('');
    setShowCreateForm(false);
    setTransitionError(null);
  };

  const handleTransition = () => {
    if (!nextStage) return;
    try {
      const updated = transitionStage(playbook, nextStage);
      setPlaybook(updated);
      setTransitionError(null);
    } catch (err: any) {
      setTransitionError(err?.message || 'Transition failed');
    }
  };

  const handleAssignRole = (role: IRRole) => {
    const userId = roleInputs[role]?.trim();
    if (!userId) return;
    const updated = assignRole(playbook, role, userId);
    setPlaybook(updated);
    setRoleInputs((prev) => ({ ...prev, [role]: '' }));
  };

  const handleUnassignRole = (role: IRRole) => {
    const updated = assignRole(playbook, role, null);
    setPlaybook(updated);
  };

  const handleAddManualNote = (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualNote.trim()) return;
    const updated = addAuditLogEntry(playbook, {
      action: 'MANUAL_NOTE',
      actorRole: 'INCIDENT_COMMANDER',
      details: manualNote.trim(),
    });
    setPlaybook(updated);
    setManualNote('');
  };

  const currentStageIndex = INCIDENT_STAGES.indexOf(playbook.stage);

  return (
    <div
      data-testid="ccpa-playbook"
      style={{
        fontFamily: 'system-ui, -apple-system, sans-serif',
        padding: '24px',
        maxWidth: '1200px',
        margin: '0 auto',
        backgroundColor: '#f8fafc',
        minHeight: '100vh',
        color: '#0f172a',
      }}
    >
      {/* Header */}
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          borderBottom: '2px solid #e2e8f0',
          paddingBottom: '16px',
          marginBottom: '24px',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h1 style={{ margin: 0, fontSize: '26px', fontWeight: '700', color: '#1e293b' }}>
              CCPA Incident Response Playbook
            </h1>
            <span
              style={{
                backgroundColor: '#dbeafe',
                color: '#1e40af',
                padding: '4px 10px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '600',
              }}
            >
              Cal. Civ. Code § 1798.82
            </span>
          </div>
          <p style={{ margin: '6px 0 0 0', color: '#64748b', fontSize: '14px' }}>
            Active Incident: <strong>{playbook.title}</strong> (ID: {playbook.id}) | Severity:{' '}
            <span
              style={{
                fontWeight: '700',
                color:
                  playbook.severity === 'CRITICAL'
                    ? '#b91c1c'
                    : playbook.severity === 'HIGH'
                    ? '#c2410c'
                    : playbook.severity === 'MEDIUM'
                    ? '#d97706'
                    : '#15803d',
              }}
            >
              {playbook.severity}
            </span>
          </p>
        </div>

        <button
          type="button"
          onClick={() => setShowCreateForm(!showCreateForm)}
          data-testid="toggle-create-btn"
          style={{
            backgroundColor: showCreateForm ? '#64748b' : '#2563eb',
            color: '#ffffff',
            padding: '8px 16px',
            borderRadius: '6px',
            border: 'none',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
          }}
        >
          {showCreateForm ? 'Cancel' : '+ New CCPA Incident'}
        </button>
      </header>

      {/* Creation Modal / Form */}
      {showCreateForm && (
        <section
          data-testid="create-incident-form"
          style={{
            backgroundColor: '#ffffff',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
            marginBottom: '24px',
            border: '1px solid #e2e8f0',
          }}
        >
          <h2 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
            Create New CCPA_BREACH Incident
          </h2>
          <form onSubmit={handleCreateIncident}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>
                  Incident Title *
                </label>
                <input
                  type="text"
                  data-testid="incident-title-input"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="e.g. Unauthorized Database Access — California Customers"
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>
                  Severity
                </label>
                <select
                  data-testid="incident-severity-select"
                  value={newSeverity}
                  onChange={(e) => setNewSeverity(e.target.value as Severity)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    fontSize: '14px',
                    backgroundColor: '#ffffff',
                    boxSizing: 'border-box',
                  }}
                >
                  {INCIDENT_SEVERITY.map((sev) => (
                    <option key={sev} value={sev}>
                      {sev}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>
                  Detection Date
                </label>
                <input
                  type="date"
                  data-testid="incident-date-input"
                  value={newDetectionDate}
                  onChange={(e) => setNewDetectionDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>
                Description / Initial Findings
              </label>
              <textarea
                data-testid="incident-desc-input"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Details of breach indicators, systems involved, and suspected data elements..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                type="submit"
                data-testid="create-ccpa-incident-btn"
                style={{
                  backgroundColor: '#16a34a',
                  color: '#ffffff',
                  padding: '8px 20px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Create Incident
              </button>
            </div>
          </form>
        </section>
      )}

      {/* CCPA 45-Day Countdown Banner */}
      <section
        data-testid="ccpa-countdown"
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          padding: '20px',
          border: '1px solid #e2e8f0',
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderLeft: '6px solid #2563eb',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span
              style={{
                backgroundColor: '#fee2e2',
                color: '#991b1b',
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: '700',
                letterSpacing: '0.5px',
              }}
            >
              CCPA STATUTORY DEADLINE
            </span>
            <span style={{ fontSize: '13px', color: '#64748b' }}>
              California Civil Code § 1798.82(a)
            </span>
          </div>
          <h2 style={{ margin: '8px 0 4px 0', fontSize: '22px', fontWeight: '700', color: '#0f172a' }}>
            {daysRemaining !== null ? `${daysRemaining} days remaining` : '45 days remaining'}
          </h2>
          <p style={{ margin: 0, fontSize: '13px', color: '#475569' }}>
            Detected on: {new Date(playbook.createdAt).toLocaleDateString()} | 45-day notification deadline:{' '}
            <strong>
              {playbook.deadline ? new Date(playbook.deadline).toLocaleDateString() : 'N/A'}
            </strong>
          </p>
        </div>

        <div style={{ textAlign: 'right' }}>
          <div
            style={{
              fontSize: '32px',
              fontWeight: '800',
              color:
                daysRemaining !== null && daysRemaining <= 7
                  ? '#dc2626'
                  : daysRemaining !== null && daysRemaining <= 15
                  ? '#d97706'
                  : '#2563eb',
            }}
          >
            {daysRemaining !== null ? `${daysRemaining}d` : '45d'}
          </div>
          <div style={{ fontSize: '12px', color: '#64748b' }}>45-Day Countdown Window</div>
        </div>
      </section>

      {/* Stage Stepper / Wizard */}
      <section
        data-testid="stage-indicator"
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          padding: '24px',
          border: '1px solid #e2e8f0',
          marginBottom: '24px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>Incident Stage Wizard</h2>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
              Current Stage:{' '}
              <strong style={{ color: '#2563eb' }}>
                {playbook.stage} — {STAGE_DESCRIPTIONS[playbook.stage]?.title}
              </strong>
            </p>
          </div>

          <button
            type="button"
            data-testid="stage-transition-btn"
            onClick={handleTransition}
            disabled={!nextStage}
            style={{
              backgroundColor: nextStage ? '#2563eb' : '#94a3b8',
              color: '#ffffff',
              padding: '10px 20px',
              borderRadius: '6px',
              border: 'none',
              fontSize: '14px',
              fontWeight: '600',
              cursor: nextStage ? 'pointer' : 'not-allowed',
              transition: 'background-color 0.2s',
            }}
          >
            {nextStage ? `Advance to ${nextStage}` : 'All Stages Complete'}
          </button>
        </div>

        {transitionError && (
          <div
            style={{
              backgroundColor: '#fee2e2',
              color: '#b91c1c',
              padding: '10px 14px',
              borderRadius: '6px',
              marginBottom: '16px',
              fontSize: '13px',
            }}
          >
            {transitionError}
          </div>
        )}

        {/* 6 Stages Stepper Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, 1fr)',
            gap: '8px',
            marginBottom: '24px',
          }}
        >
          {INCIDENT_STAGES.map((stg, index) => {
            const isCurrent = stg === playbook.stage;
            const isCompleted = index < currentStageIndex;
            return (
              <div
                key={stg}
                style={{
                  padding: '12px 8px',
                  borderRadius: '6px',
                  textAlign: 'center',
                  backgroundColor: isCurrent ? '#eff6ff' : isCompleted ? '#f0fdf4' : '#f8fafc',
                  border: isCurrent
                    ? '2px solid #2563eb'
                    : isCompleted
                    ? '1px solid #86efac'
                    : '1px solid #e2e8f0',
                }}
              >
                <div
                  style={{
                    fontSize: '11px',
                    fontWeight: '700',
                    color: isCurrent ? '#2563eb' : isCompleted ? '#16a34a' : '#94a3b8',
                    marginBottom: '4px',
                  }}
                >
                  STEP {index + 1}
                </div>
                <div
                  style={{
                    fontSize: '12px',
                    fontWeight: '700',
                    color: isCurrent ? '#1e3a8a' : isCompleted ? '#166534' : '#475569',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {stg}
                </div>
                <div
                  style={{
                    fontSize: '10px',
                    color: isCurrent ? '#3b82f6' : isCompleted ? '#22c55e' : '#94a3b8',
                    marginTop: '2px',
                  }}
                >
                  {isCurrent ? '● Active' : isCompleted ? '✓ Done' : 'Pending'}
                </div>
              </div>
            );
          })}
        </div>

        {/* Current Stage Guidance & Checklist */}
        <div
          style={{
            backgroundColor: '#f8fafc',
            borderRadius: '6px',
            padding: '16px',
            border: '1px solid #e2e8f0',
          }}
        >
          <h3 style={{ margin: '0 0 6px 0', fontSize: '15px', fontWeight: '700', color: '#1e293b' }}>
            Stage Guidance: {STAGE_DESCRIPTIONS[playbook.stage]?.title}
          </h3>
          <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#64748b' }}>
            {STAGE_DESCRIPTIONS[playbook.stage]?.summary}
          </p>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: '600', color: '#334155' }}>
            Mandatory Action Checklist:
          </h4>
          <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#475569' }}>
            {CCPA_STAGE_CHECKLISTS[playbook.stage]?.map((item, idx) => (
              <li key={idx} style={{ marginBottom: '4px' }}>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Bottom 2-Column: Role Assignment & Audit Log */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        {/* Role Assignment Panel */}
        <section
          data-testid="role-assignment"
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            padding: '20px',
            border: '1px solid #e2e8f0',
          }}
        >
          <h2 style={{ margin: '0 0 4px 0', fontSize: '17px', fontWeight: '700' }}>
            Incident Response Roles
          </h2>
          <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#64748b' }}>
            Assign key leads to coordinate CCPA statutory response and legal reporting.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {IR_ROLES.map((role) => {
              const assignedUser = playbook.roles[role];
              return (
                <div
                  key={role}
                  style={{
                    padding: '12px',
                    borderRadius: '6px',
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '8px',
                    }}
                  >
                    <span style={{ fontSize: '13px', fontWeight: '700', color: '#334155' }}>
                      {role.replace('_', ' ')}
                    </span>
                    <span
                      style={{
                        fontSize: '12px',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        backgroundColor: assignedUser ? '#dcfce7' : '#f1f5f9',
                        color: assignedUser ? '#166534' : '#64748b',
                        fontWeight: '600',
                      }}
                    >
                      {assignedUser ? `Assigned: ${assignedUser}` : 'Unassigned'}
                    </span>
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      data-testid={`role-input-${role}`}
                      placeholder={`Assign user to ${role}`}
                      value={roleInputs[role]}
                      onChange={(e) =>
                        setRoleInputs({ ...roleInputs, [role]: e.target.value })
                      }
                      style={{
                        flex: 1,
                        padding: '6px 10px',
                        borderRadius: '4px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px',
                      }}
                    />
                    <button
                      type="button"
                      data-testid={`role-assign-btn-${role}`}
                      onClick={() => handleAssignRole(role)}
                      style={{
                        backgroundColor: '#2563eb',
                        color: '#ffffff',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        border: 'none',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                      }}
                    >
                      Assign
                    </button>
                    {assignedUser && (
                      <button
                        type="button"
                        data-testid={`role-unassign-btn-${role}`}
                        onClick={() => handleUnassignRole(role)}
                        style={{
                          backgroundColor: '#ef4444',
                          color: '#ffffff',
                          padding: '6px 10px',
                          borderRadius: '4px',
                          border: 'none',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                        }}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Audit Log Panel */}
        <section
          data-testid="audit-log"
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            padding: '20px',
            border: '1px solid #e2e8f0',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <h2 style={{ margin: '0 0 4px 0', fontSize: '17px', fontWeight: '700' }}>
            Incident Audit Log ({playbook.auditLog.length})
          </h2>
          <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#64748b' }}>
            Immutable chronological record of decisions, stage changes, and role assignments.
          </p>

          {/* Add Manual Note */}
          <form onSubmit={handleAddManualNote} style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <input
              type="text"
              data-testid="audit-note-input"
              placeholder="Add audit note or forensic observation..."
              value={manualNote}
              onChange={(e) => setManualNote(e.target.value)}
              style={{
                flex: 1,
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                fontSize: '13px',
              }}
            />
            <button
              type="submit"
              data-testid="add-audit-btn"
              style={{
                backgroundColor: '#334155',
                color: '#ffffff',
                padding: '8px 14px',
                borderRadius: '6px',
                border: 'none',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Add Log
            </button>
          </form>

          {/* Audit Entries List */}
          <div
            style={{
              flex: 1,
              maxHeight: '400px',
              overflowY: 'auto',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              padding: '12px',
              backgroundColor: '#f8fafc',
            }}
          >
            {playbook.auditLog.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '13px', padding: '20px' }}>
                No audit entries recorded yet.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column-reverse', gap: '10px' }}>
                {playbook.auditLog.map((entry, idx) => (
                  <div
                    key={idx}
                    data-testid={`audit-entry-${idx}`}
                    style={{
                      padding: '10px',
                      borderRadius: '6px',
                      backgroundColor: '#ffffff',
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '4px',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '11px',
                          fontWeight: '700',
                          color: '#2563eb',
                          backgroundColor: '#eff6ff',
                          padding: '2px 6px',
                          borderRadius: '4px',
                        }}
                      >
                        {entry.action}
                      </span>
                      <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                        {new Date(entry.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#1e293b', marginBottom: '2px' }}>
                      {entry.details}
                    </div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>
                      Actor: <strong>{entry.actorRole}</strong>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default CcpaPlaybook;
